import { Component, EventEmitter, Input, Output } from '@angular/core';
import * as moment from 'moment';
import { UtilsService } from 'projects/app-core/src/service/utils.service';
import { ForumTags } from '../../../models/events/AnyoForums';
import {
  ForumListAdminResponse,
  ForumsListFilterData,
} from '../../../models/events/forumAdminGridResponse';
import {
  CheckboxOptions,
  DateFilterEmitData,
} from '../events-table/events-table.component';

@Component({
  selector: 'app-forum-table',
  templateUrl: './forum-table.component.html',
  styleUrl: './forum-table.component.scss',
})
export class ForumTableComponent {
  @Input() forumTableData: ForumListAdminResponse[] | undefined;
  @Input() forumTotalPages: number | undefined;
  @Input() pageSize: number | undefined;
  @Input() pageNum: number | undefined;
  @Input() hideTestEvent: boolean = false;
  @Output() resetFilterEvent = new EventEmitter<void>();
  @Output() pageSizeChangeEvent = new EventEmitter<number>();
  @Output() pageChangeEvent = new EventEmitter<number>();
  @Output() categoryFilterEvent = new EventEmitter<string[]>();
  @Output() paymentTypeFilterEvent = new EventEmitter<string[]>();
  @Output() eventsDateFilterEvent = new EventEmitter<DateFilterEmitData>();
  @Output() hideTestFilterEvent = new EventEmitter<boolean>();
  @Output() presenterFilterEvent = new EventEmitter<string[]>();
  @Output() activeFilterEvent = new EventEmitter<string[]>();
  @Output() searchEvent = new EventEmitter<string>();
  public forumTags = ForumTags;
  dateFormat = 'dd/MM/yyyy';
  categoryCheckBoxOptions: CheckboxOptions[] = [];
  paymentTypeCheckBoxOptions: CheckboxOptions[] = [];
  presenterCheckBoxOptions: CheckboxOptions[] = [];
  dateCheckBoxOptions: CheckboxOptions[] = [
    {
      label: 'Forum Start Date',
      value: 'forumStartDate',
      checked: false,
    },
    {
      label: 'Registration Cut Off Date',
      value: 'registrationCutOffDate',
      checked: false,
    },
    {
      label: 'Visible From',
      value: 'visibleFrom',
      checked: false,
    },
  ];
  pageSizeOptions = [10, 20, 50, 100];
  selectedCategoryString: string[] = [];
  selectedPresenterString: string[] = [];
  selectedPaymentTypeString: string[] = [];
  selectedActiveString: string[] = [];
  globalSearchInput = '';
  activeCheckBoxOptions: CheckboxOptions[] = [
    { label: 'Active', value: 'true', checked: false },
    { label: 'Inactive', value: 'false', checked: false },
  ];

  eventsColumnFilterDate: Date[] | undefined;

  disabledDate = (current: Date) => {
    return moment(current).isBefore(moment().startOf("day"));
  };

  openForumRegistration(id: string) {
    window.open(`/events/forums/registrations?forumId=${id}`, '_blank');
  }

  searchGlobal() {
    if (this.globalSearchInput.trim().length) {
      this.searchEvent.emit(this.globalSearchInput.trim());
    }
  }

  resetSearch() {
    this.globalSearchInput = '';
  }

  resetValues() {
    this.categoryCheckBoxOptions = [];
    this.presenterCheckBoxOptions = [];
    this.paymentTypeCheckBoxOptions = [];
    this.selectedCategoryString = [];
    this.selectedPresenterString = [];
    this.selectedPaymentTypeString = [];
    this.selectedActiveString = [];
    this.eventsColumnFilterDate = undefined;
    this.dateFilter = [];
    this.activeCheckBoxOptions = this.activeCheckBoxOptions.map(opt => ({ ...opt, checked: false }));
    this.dateCheckBoxOptions = this.dateCheckBoxOptions.map(opt => ({ ...opt, checked: false }));
  }

  resetPresenterFilter() {
    this.presenterCheckBoxOptions = [];
    this.selectedPresenterString = [];
    this.presenterFilterEvent.emit([]);
  }

  resetCategoryFilter() {
    this.categoryCheckBoxOptions = [];
    this.selectedCategoryString = [];
    this.categoryFilterEvent.emit([]);
  }

  resetPaymentTypeFilter() {
    this.paymentTypeCheckBoxOptions = [];
    this.selectedPaymentTypeString = [];
    this.paymentTypeFilterEvent.emit([]);
  }

  resetStatusFilter() {
    this.selectedActiveString = [];
    this.activeCheckBoxOptions = this.activeCheckBoxOptions.map(opt => ({ ...opt, checked: false }));
    this.activeFilterEvent.emit([]);
  }

  resetDateFilter() {
    this.eventsColumnFilterDate = undefined;
    this.dateFilter = [];
    this.dateCheckBoxOptions = this.dateCheckBoxOptions.map(opt => ({ ...opt, checked: false }));
    this.eventsDateFilterEvent.emit({ dates: undefined, options: [] });
  }

  resetFilter() {
    this.resetFilterEvent.emit();
  }

  nzPageIndexChange(page: number) {
    this.pageChangeEvent.emit(page);
  }

  nzPageSizeChange(pageSize: number) {
    this.pageSizeChangeEvent.emit(pageSize);
  }

  categoryFilterFn() {
    this.categoryFilterEvent.emit(this.selectedCategoryString);
  }

  categoryFilterChangeFn($event: string[]) {
    this.selectedCategoryString = $event;
  }

  setCategoryFilterData(filterOptions?: ForumsListFilterData) {
    if (this.categoryCheckBoxOptions.length === 0 && filterOptions?.category) {
      this.categoryCheckBoxOptions = filterOptions.category.map((data) => ({
        checked: this.selectedCategoryString.includes(data.categoryId),
        label: data.categoryName,
        value: data.categoryId,
      }));
    } else if (this.categoryCheckBoxOptions.length > 0) {
      this.categoryCheckBoxOptions.forEach((opt) => {
        opt.checked = this.selectedCategoryString.includes(opt.value);
      });
    }
  }

  presenterFilterFn() {
    this.presenterFilterEvent.emit(this.selectedPresenterString);
  }

  presenterFilterChangeFn($event: string[]) {
    this.selectedPresenterString = $event;
  }

  setPresenterFilterData(filterOptions?: ForumsListFilterData) {
    if (this.presenterCheckBoxOptions.length === 0 && filterOptions?.presenters) {
      this.presenterCheckBoxOptions = filterOptions.presenters.map((data) => ({
        checked: this.selectedPresenterString.includes(data.presenterId),
        label: data.presenterName,
        value: data.presenterId,
      }));
    } else if (this.presenterCheckBoxOptions.length > 0) {
      this.presenterCheckBoxOptions.forEach((opt) => {
        opt.checked = this.selectedPresenterString.includes(opt.value);
      });
    }
  }

  paymentTypeFilterFn() {
    this.paymentTypeFilterEvent.emit(this.selectedPaymentTypeString);
  }

  paymentTypeFilterChangeFn($event: string[]) {
    this.selectedPaymentTypeString = $event;
  }

  activeFilterFn() {
    this.activeFilterEvent.emit(this.selectedActiveString);
  }

  activeFilterChangeFn($event: string[]) {
    this.selectedActiveString = $event;
  }

  setPaymentTypeFilterData(filterOptions?: ForumsListFilterData) {
    if (this.paymentTypeCheckBoxOptions.length === 0 && filterOptions?.paymentTypes) {
      this.paymentTypeCheckBoxOptions = filterOptions.paymentTypes.map((data) => ({
        checked: this.selectedPaymentTypeString.includes(data),
        label: this.utils.convertToTitleCase(data),
        value: data,
      }));
    } else if (this.paymentTypeCheckBoxOptions.length > 0) {
      this.paymentTypeCheckBoxOptions.forEach((opt) => {
        opt.checked = this.selectedPaymentTypeString.includes(opt.value);
      });
    }
  }

  dateFilterFn() {
    const data: DateFilterEmitData = {
      dates: this.eventsColumnFilterDate,
      options: this.dateFilter,
    };
    this.eventsDateFilterEvent.emit(data);
  }

  dateFilter!: string[];

  dateFilterChangeFn($event: string[]) {
    this.dateFilter = $event;
  }

  onEventsFilterDateChange($event: Date[]) {
    this.eventsColumnFilterDate = $event;
  }

  testEventChangeFn($event: boolean) {
    this.hideTestEvent = $event;
    this.hideTestFilterEvent.emit($event);
  }

  constructor(public utils: UtilsService) { }
}
